"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Segment = exports.segmentAttributesWithComposedAttributes = exports.segmentAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const SAttr = __importStar(require("./attributeTypes/SegmentAttributes"));
const Junction_1 = require("./Junction");
const Routing_1 = require("./Routing");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const StartEndable_1 = require("./StartEndable");
const AnswerStatus_1 = require("./attributeTypes/AnswerStatus");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.segmentAttributes = [
    ...StartEndable_1.startEndDateAndTimesAttributes,
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    '_sequence',
    'mode',
    'modeOtherSpecify',
    'driverType',
    'driverUuid',
    'carType',
    'vehicleOccupancy',
    'paidForParking',
    'onDemandType',
    'busLines',
    'stations',
    'preData'
];
exports.segmentAttributesWithComposedAttributes = [
    ...exports.segmentAttributes,
    '_origin',
    '_destination',
    '_transitDeclaredRouting',
    '_walkingDeclaredRouting',
    '_cyclingDeclaredRouting',
    '_drivingDeclaredRouting',
    '_transitCalculatedRoutings',
    '_walkingCalculatedRoutings',
    '_cyclingCalculatedRoutings',
    '_drivingCalculatedRoutings'
];
/**
 * A segment is a part of a trip using a single unique mode
 * Segments can have a start junction and an end junction
 * which are the departure and arrival places of the segment
 * like subway station, a parking or another or the trip origin
 * and/or destination when the segment is first or last for the trip
 */
class Segment extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, [
            '_origin',
            '_destination',
            '_transitDeclaredRouting',
            '_walkingDeclaredRouting',
            '_cyclingDeclaredRouting',
            '_drivingDeclaredRouting',
            '_transitCalculatedRoutings',
            '_walkingCalculatedRoutings',
            '_cyclingCalculatedRoutings',
            '_drivingCalculatedRoutings',
            'origin',
            'destination',
            'transitDeclaredRouting',
            'walkingDeclaredRouting',
            'cyclingDeclaredRouting',
            'drivingDeclaredRouting',
            'transitCalculatedRoutings',
            'walkingCalculatedRoutings',
            'cyclingCalculatedRoutings',
            'drivingCalculatedRoutings',
            '_tripUuid'
        ]), exports.segmentAttributes, exports.segmentAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.origin = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._origin, Junction_1.Junction.unserialize, this._surveyObjectsRegistry);
        this.destination = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._destination, Junction_1.Junction.unserialize, this._surveyObjectsRegistry);
        this.transitDeclaredRouting = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._transitDeclaredRouting, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.walkingDeclaredRouting = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._walkingDeclaredRouting, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.cyclingDeclaredRouting = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._cyclingDeclaredRouting, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.drivingDeclaredRouting = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._drivingDeclaredRouting, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.transitCalculatedRoutings = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._transitCalculatedRoutings, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.walkingCalculatedRoutings = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._walkingCalculatedRoutings, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.cyclingCalculatedRoutings = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._cyclingCalculatedRoutings, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.drivingCalculatedRoutings = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._drivingCalculatedRoutings, Routing_1.Routing.unserialize, this._surveyObjectsRegistry);
        this.tripUuid = params._tripUuid;
        this._surveyObjectsRegistry.registerSegment(this);
    }
    /**
     * Checks if the segment is a transit segment
     * @returns True if the segment is a transit segment, false otherwise
     */
    isTransit() {
        return this.modeCategory === 'transit';
    }
    get modeCategory() {
        return this.mode ? SAttr.mapModeToModeCategory[this.mode] : undefined;
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get mode() {
        return this._attributes.mode;
    }
    set mode(value) {
        this._attributes.mode = value;
    }
    get modeOtherSpecify() {
        return this._attributes.modeOtherSpecify;
    }
    set modeOtherSpecify(value) {
        this._attributes.modeOtherSpecify = value;
    }
    get startDate() {
        return this._attributes.startDate;
    }
    set startDate(value) {
        this._attributes.startDate = value;
    }
    get startTime() {
        return this._attributes.startTime;
    }
    set startTime(value) {
        this._attributes.startTime = value;
    }
    get startTimePeriod() {
        return this._attributes.startTimePeriod;
    }
    set startTimePeriod(value) {
        this._attributes.startTimePeriod = value;
    }
    get endDate() {
        return this._attributes.endDate;
    }
    set endDate(value) {
        this._attributes.endDate = value;
    }
    get endTime() {
        return this._attributes.endTime;
    }
    set endTime(value) {
        this._attributes.endTime = value;
    }
    get endTimePeriod() {
        return this._attributes.endTimePeriod;
    }
    set endTimePeriod(value) {
        this._attributes.endTimePeriod = value;
    }
    get driverType() {
        return this._attributes.driverType;
    }
    set driverType(value) {
        this._attributes.driverType = value;
    }
    get driverUuid() {
        return this._attributes.driverUuid;
    }
    set driverUuid(value) {
        this._attributes.driverUuid = value;
    }
    get vehicleOccupancy() {
        return this._attributes.vehicleOccupancy;
    }
    set vehicleOccupancy(value) {
        this._attributes.vehicleOccupancy = value;
    }
    get carType() {
        return this._attributes.carType;
    }
    set carType(value) {
        this._attributes.carType = value;
    }
    get paidForParking() {
        return this._attributes.paidForParking;
    }
    set paidForParking(value) {
        this._attributes.paidForParking = value;
    }
    get onDemandType() {
        return this._attributes.onDemandType;
    }
    set onDemandType(value) {
        this._attributes.onDemandType = value;
    }
    get busLines() {
        return this._attributes.busLines;
    }
    set busLines(value) {
        this._attributes.busLines = value;
    }
    get stations() {
        return this._attributes.stations;
    }
    set stations(value) {
        this._attributes.stations = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get origin() {
        return this._origin;
    }
    set origin(value) {
        this._origin = value;
    }
    get destination() {
        return this._destination;
    }
    set destination(value) {
        this._destination = value;
    }
    get transitDeclaredRouting() {
        return this._transitDeclaredRouting;
    }
    set transitDeclaredRouting(value) {
        this._transitDeclaredRouting = value;
    }
    get walkingDeclaredRouting() {
        return this._walkingDeclaredRouting;
    }
    set walkingDeclaredRouting(value) {
        this._walkingDeclaredRouting = value;
    }
    get cyclingDeclaredRouting() {
        return this._cyclingDeclaredRouting;
    }
    set cyclingDeclaredRouting(value) {
        this._cyclingDeclaredRouting = value;
    }
    get drivingDeclaredRouting() {
        return this._drivingDeclaredRouting;
    }
    set drivingDeclaredRouting(value) {
        this._drivingDeclaredRouting = value;
    }
    get transitCalculatedRoutings() {
        return this._transitCalculatedRoutings;
    }
    set transitCalculatedRoutings(value) {
        this._transitCalculatedRoutings = value;
    }
    get walkingCalculatedRoutings() {
        return this._walkingCalculatedRoutings;
    }
    set walkingCalculatedRoutings(value) {
        this._walkingCalculatedRoutings = value;
    }
    get cyclingCalculatedRoutings() {
        return this._cyclingCalculatedRoutings;
    }
    set cyclingCalculatedRoutings(value) {
        this._cyclingCalculatedRoutings = value;
    }
    get drivingCalculatedRoutings() {
        return this._drivingCalculatedRoutings;
    }
    set drivingCalculatedRoutings(value) {
        this._drivingCalculatedRoutings = value;
    }
    get tripUuid() {
        return this._tripUuid;
    }
    set tripUuid(value) {
        this._tripUuid = value;
    }
    /**
     * Questionnaire answer: whether another mode follows this segment.
     * Not a Segment attribute; copied from the response hashmap by SegmentFactory.
     */
    get hasNextMode() {
        return this._hasNextMode;
    }
    set hasNextMode(value) {
        this._hasNextMode = value;
    }
    get trip() {
        if (!this._tripUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getTrip(this._tripUuid);
    }
    get journey() {
        var _a;
        return (_a = this.trip) === null || _a === void 0 ? void 0 : _a.journey;
    }
    get person() {
        var _a, _b;
        return (_b = (_a = this.trip) === null || _a === void 0 ? void 0 : _a.journey) === null || _b === void 0 ? void 0 : _b.person;
    }
    get household() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.trip) === null || _a === void 0 ? void 0 : _a.journey) === null || _b === void 0 ? void 0 : _b.person) === null || _c === void 0 ? void 0 : _c.household;
    }
    /**
     * Creates a Segment object from sanitized parameters
     * @param {ExtendedSegmentAttributes | SerializedExtendedSegmentAttributes} params - Sanitized segment parameters
     * @returns {Segment} New Segment instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Segment(flattenedParams, surveyObjectsRegistry);
    }
    /**
     * Wrap the answers that a questionnaire stores as plain values in their
     * status, and leave the ones already wrapped as they are. Surveys write
     * these two answers directly in the response, so they reach `create`
     * unwrapped. A blank one is omitted so that it does not read as an answer
     * of the wrong shape. A value that is not a number and not a known
     * non-response is left as it is, for `validateParams` to reject.
     *
     * @param {Object} dirtyParams The parameters as read from the response
     * @returns {Object} A copy of the parameters, with those answers wrapped.
     * Parameters that are not an object are returned as they are, for
     * `validateParams` to report.
     */
    static wrapAnswerStatuses(dirtyParams) {
        if (typeof dirtyParams !== 'object' || dirtyParams === null) {
            return dirtyParams;
        }
        const vehicleOccupancy = (0, AnswerStatus_1.toNumberAnswerStatus)(dirtyParams.vehicleOccupancy);
        const paidForParking = (0, AnswerStatus_1.toAnswerStatus)(dirtyParams.paidForParking);
        const params = Object.assign(Object.assign({}, dirtyParams), { 
            // Keep a value that could not be wrapped as a number, so validation rejects it
            vehicleOccupancy: vehicleOccupancy !== undefined ? vehicleOccupancy : dirtyParams.vehicleOccupancy, paidForParking });
        // An answer that wraps to nothing leaves its attribute absent, rather than present and empty
        if ((0, LodashExtensions_1._isBlank)(params.vehicleOccupancy)) {
            delete params.vehicleOccupancy;
        }
        if (paidForParking === undefined) {
            delete params.paidForParking;
        }
        return params;
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const params = Segment.wrapAnswerStatuses(dirtyParams);
        const errors = Segment.validateParams(params);
        const segment = errors.length === 0 ? new Segment(params, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(segment);
    }
    /**
     * Validates attributes types for Segment.
     * @param dirtyParams The parameters to validate.
     * @param displayName The name of the object to validate, for error display
     * @returns Error[]
     */
    static validateParams(dirtyParams, displayName = 'Segment') {
        const errors = [];
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams, displayName));
        errors.push(...StartEndable_1.StartEndable.validateParams(dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('_sequence', dirtyParams._sequence, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('mode', dirtyParams.mode, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('modeOtherSpecify', dirtyParams.modeOtherSpecify, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('driverType', dirtyParams.driverType, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('driverUuid', dirtyParams.driverUuid, displayName));
        errors.push(...(0, AnswerStatus_1.validateAnswerStatus)('vehicleOccupancy', dirtyParams.vehicleOccupancy, displayName, ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('carType', dirtyParams.carType, displayName));
        errors.push(...(0, AnswerStatus_1.validateAnswerStatus)('paidForParking', dirtyParams.paidForParking, displayName, ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('onDemandType', dirtyParams.onDemandType, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isArrayOfStrings('busLines', dirtyParams.busLines, displayName));
        // isArrayOfStrings treats null as absent. A present stations value must be an array of strings.
        if (dirtyParams.stations === null) {
            errors.push(new Error(`${displayName} validateParams: stations should be an array of strings`));
        }
        else {
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isArrayOfStrings('stations', dirtyParams.stations, displayName));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        const transitDeclaredRoutingAttributes = dirtyParams._transitDeclaredRouting;
        if (transitDeclaredRoutingAttributes !== undefined) {
            errors.push(...Routing_1.Routing.validateParams(transitDeclaredRoutingAttributes, 'TransitRouting'));
        }
        const walkingDeclaredRoutingAttributes = dirtyParams._walkingDeclaredRouting;
        if (walkingDeclaredRoutingAttributes !== undefined) {
            errors.push(...Routing_1.Routing.validateParams(walkingDeclaredRoutingAttributes, 'WalkingRouting'));
        }
        const cyclingDeclaredRoutingAttributes = dirtyParams._cyclingDeclaredRouting;
        if (cyclingDeclaredRoutingAttributes !== undefined) {
            errors.push(...Routing_1.Routing.validateParams(cyclingDeclaredRoutingAttributes, 'CyclingRouting'));
        }
        const drivingDeclaredRoutingAttributes = dirtyParams._drivingDeclaredRouting;
        if (drivingDeclaredRoutingAttributes !== undefined) {
            errors.push(...Routing_1.Routing.validateParams(drivingDeclaredRoutingAttributes, 'DrivingRouting'));
        }
        const transitCalculatedRoutingsAttributes = dirtyParams._transitCalculatedRoutings !== undefined
            ? dirtyParams._transitCalculatedRoutings
            : [];
        for (let i = 0, countI = transitCalculatedRoutingsAttributes.length; i < countI; i++) {
            errors.push(...Routing_1.Routing.validateParams(transitCalculatedRoutingsAttributes[i], 'TransitRouting'));
        }
        const walkingCalculatedRoutingsAttributes = dirtyParams._walkingCalculatedRoutings !== undefined
            ? dirtyParams._walkingCalculatedRoutings
            : [];
        for (let i = 0, countI = walkingCalculatedRoutingsAttributes.length; i < countI; i++) {
            errors.push(...Routing_1.Routing.validateParams(walkingCalculatedRoutingsAttributes[i], 'WalkingRouting'));
        }
        const cyclingCalculatedRoutingsAttributes = dirtyParams._cyclingCalculatedRoutings !== undefined
            ? dirtyParams._cyclingCalculatedRoutings
            : [];
        for (let i = 0, countI = cyclingCalculatedRoutingsAttributes.length; i < countI; i++) {
            errors.push(...Routing_1.Routing.validateParams(cyclingCalculatedRoutingsAttributes[i], 'CyclingRouting'));
        }
        const drivingCalculatedRoutingsAttributes = dirtyParams._drivingCalculatedRoutings !== undefined
            ? dirtyParams._drivingCalculatedRoutings
            : [];
        for (let i = 0, countI = drivingCalculatedRoutingsAttributes.length; i < countI; i++) {
            errors.push(...Routing_1.Routing.validateParams(drivingCalculatedRoutingsAttributes[i], 'DrivingRouting'));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_tripUuid', dirtyParams._tripUuid, displayName));
        return errors;
    }
}
exports.Segment = Segment;
Segment._confidentialAttributes = ['preData'];
//# sourceMappingURL=Segment.js.map